import React, { useCallback, useEffect, useState } from 'react';
import { federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import FederationGate from './FederationGate';
import {
  ErrorBanner,
  LoadingBlock,
  OBJECTIVE_STATUSES,
  PageHeader,
  btnPrimary,
  btnSecondary,
  canWrite,
  inputClass,
  isViewer,
  safeText,
} from './federationShared';

function ObjectivesInner() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const viewer = isViewer(user);

  const [objectives, setObjectives] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    programId: '',
    title: '',
    description: '',
    status: 'Active',
    startDate: '',
    targetReviewDate: '',
  });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (programFilter) params.programId = programFilter;
    Promise.all([
      federation.getObjectives(params),
      federation.getPrograms().catch(() => ({ programs: [] })),
    ])
      .then(([o, p]) => {
        setObjectives(o.objectives || []);
        setPrograms(p.programs || []);
      })
      .catch((e) => {
        setObjectives([]);
        setError(e.message || 'Could not load objectives');
      })
      .finally(() => setLoading(false));
  }, [programFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({
      programId: programFilter || '',
      title: '',
      description: '',
      status: 'Active',
      startDate: '',
      targetReviewDate: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (objective) => {
    setEditingId(objective.id);
    setShowForm(true);
    setForm({
      programId: objective.programId || '',
      title: objective.title || '',
      description: objective.description || '',
      status: objective.status || 'Active',
      startDate: objective.startDate || '',
      targetReviewDate: objective.targetReviewDate || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!writable) return;
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!editingId && !form.programId) {
      setError('Program is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await federation.updateObjective({
          id: editingId,
          title: form.title.trim(),
          description: form.description.trim() || null,
          status: form.status,
          startDate: form.startDate || null,
          targetReviewDate: form.targetReviewDate || null,
        });
      } else {
        await federation.createObjective({
          programId: form.programId,
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          status: form.status === 'Achieved' ? 'Active' : form.status,
          startDate: form.startDate || undefined,
          targetReviewDate: form.targetReviewDate || undefined,
        });
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message || 'Could not save objective');
    } finally {
      setSaving(false);
    }
  };

  const programName = (id) => {
    const p = programs.find((x) => x.id === id);
    return p ? safeText(p.name) : safeText(id, '—');
  };

  return (
    <>
      <PageHeader
        title="Objectives"
        description="Development objectives linked to Federation programs."
        actions={
          writable && !viewer ? (
            <button
              type="button"
              className={btnPrimary}
              onClick={() => {
                if (showForm) resetForm();
                else {
                  setEditingId(null);
                  setForm((f) => ({ ...f, programId: programFilter || f.programId }));
                  setShowForm(true);
                }
              }}
            >
              {showForm ? 'Cancel' : 'Create objective'}
            </button>
          ) : null
        }
      />

      <div className="mb-6 max-w-xs">
        <label className="block text-xs font-medium text-gray-500 mb-1">Program</label>
        <select
          className={inputClass}
          value={programFilter}
          onChange={(e) => setProgramFilter(e.target.value)}
        >
          <option value="">All programs</option>
          {programs.map((p) => (
            <option key={p.id} value={p.id}>{safeText(p.name)}</option>
          ))}
        </select>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {showForm && writable && (
        <form onSubmit={handleSubmit} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          <h2 className="text-base font-semibold text-navy-900">
            {editingId ? 'Edit objective' : 'New objective'}
          </h2>
          {!editingId && (
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Program</label>
              <select
                className={inputClass}
                value={form.programId}
                onChange={(e) => setForm((f) => ({ ...f, programId: e.target.value }))}
                required
              >
                <option value="">Select program</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>{safeText(p.name)}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Title</label>
            <input
              className={inputClass}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={200}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Description</label>
            <textarea
              className={inputClass}
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={4000}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Status</label>
            <select
              className={inputClass}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {OBJECTIVE_STATUSES.filter((s) => editingId || s !== 'Achieved').map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {editingId && form.status === 'Achieved' && (
              <p className="text-xs text-gray-500 mt-1">
                Achieved is a leadership declaration — not inferred from reports alone.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Start date</label>
              <input
                type="date"
                className={inputClass}
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Target review</label>
              <input
                type="date"
                className={inputClass}
                value={form.targetReviewDate}
                onChange={(e) => setForm((f) => ({ ...f, targetReviewDate: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create objective'}
            </button>
            <button type="button" className={btnSecondary} onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <LoadingBlock label="Loading objectives…" />}

      {!loading && objectives.length === 0 && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No objectives</p>
          <p className="text-sm text-gray-500 mt-2">
            Link development objectives to a program after teams are participating.
          </p>
        </div>
      )}

      {!loading && objectives.length > 0 && (
        <ul className="space-y-3">
          {objectives.map((objective) => (
            <li key={objective.id} className="border border-navy-200 rounded-xl bg-white px-4 py-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <p className="font-medium text-navy-900">{safeText(objective.title)}</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {programName(objective.programId)} · {safeText(objective.status)}
                  </p>
                  {objective.description ? (
                    <p className="text-sm text-navy-800 mt-2 whitespace-pre-wrap">
                      {safeText(objective.description)}
                    </p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-gray-500">
                    {objective.startDate ? <span>Start: {safeText(objective.startDate)}</span> : null}
                    {objective.targetReviewDate ? (
                      <span>Review: {safeText(objective.targetReviewDate)}</span>
                    ) : null}
                  </div>
                </div>
                {writable && (
                  <button type="button" className={btnSecondary} onClick={() => startEdit(objective)}>
                    Edit
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function FederationObjectives() {
  return (
    <FederationGate>
      <ObjectivesInner />
    </FederationGate>
  );
}
